/**
 * Hungarian (hu) translations.
 *
 * Same key shape as the Slovak source dictionary. `Dictionary` is already the
 * *widened* shape (string literals → `string`, see `Widen` in `sk.ts`), so a
 * translated dictionary is assigned directly via `: Dictionary` — exactly like
 * the other locales (en/de/cs/pl/fr). The annotation structurally validates
 * every key (missing, extra, or mistyped — including nested ones like
 * `calendar.types.doctor`) at compile time.
 *
 * "Solandio" is treated as an indeclinable brand name — never declined.
 * Do NOT change the `Dictionary` shape — only translate values.
 */
import { type Dictionary } from './sk';

export const hu: Dictionary = {
  appName: 'Solandio',
  tagline: 'Napfényes föld a gyermekeiteknek és a családotoknak.',

  language: {
    label: 'Nyelv',
    select: 'Nyelv kiválasztása',
  },

  common: {
    save: 'Mentés',
    saving: 'Mentés…',
    cancel: 'Mégse',
    add: 'Hozzáadás',
    edit: 'Szerkesztés',
    delete: 'Törlés',
    remove: 'Eltávolítás',
    close: 'Bezárás',
    loading: 'Betöltés…',
    none: 'Nincs',
    optional: 'nem kötelező',
    back: 'Vissza',
    today: 'Ma',
    note: 'Megjegyzés',
    required: 'Ez a mező kötelező.',
    error: 'Valami elromlott.',
    retry: 'Újrapróbálkozás',
    confirmDelete: 'Biztosan eltávolítja?',
    skipToContent: 'Ugrás a tartalomra',
  },

  auth: {
    signInTitle: 'Üdvözöljük a Solandio világában',
    signInLead:
      'Nyugodt, áttekinthető hely, ahol az egész család rendben van — naptár, gyerekek, bevásárlás és költségek.',
    signInMicrosoft: 'Bejelentkezés vagy regisztráció',
    signInGoogle: 'Bejelentkezés Google-fiókkal',
    signInMock: 'Bejelentkezés (helyi fejlesztés)',
    signOut: 'Kijelentkezés',
    signedInAs: 'Bejelentkezve mint',
    loadingSession: 'Bejelentkezés ellenőrzése…',
    warmingUp:
      'A Solandio előkészítése folyamatban… hosszabb szünet utáni első megnyitáskor ez néhány másodpercet igénybe vehet.',
    devNotice:
      'Helyi fejlesztői mód — Azure nélkül. Az adatok kizárólag a böngészőjében tárolódnak.',
  },

  nav: {
    home: 'Kezdőlap',
    children: 'Gyerekek',
    contacts: 'Kapcsolatok',
    health: 'Egészség',
    calendar: 'Naptár',
    shopping: 'Bevásárlás',
    documents: 'Dokumentumok',
    messages: 'Üzenetek',
    expenses: 'Költségek',
    agreements: 'Megállapodások',
    sharing: 'Család',
    audit: 'Napló',
    settings: 'Beállítások',
    modules: 'Modulok',
    menu: 'Menü',
  },

  settings: {
    title: 'Beállítások',
    lead: 'Kezelje profilját, nyelvét és bejelentkezését.',
    profile: 'Profil',
    displayName: 'Megjelenített név',
    displayNameHint: 'Ezt a nevet látják a többi családtag.',
    email: 'E-mail',
    emailHint: 'Az e-mail-cím nem módosítható — a bejelentkezéséhez kapcsolódik.',
    nameInvalid: 'Adjon meg egy nevet (1–120 karakter).',
    saved: 'Mentve.',
    preferences: 'Beállítások',
    account: 'Fiók',
    signOutHint: 'Jelentkezzen ki erről az eszközről.',
  },

  profileSetup: {
    title: 'Üdvözöljük a Solandióban!',
    lead: 'Mielőtt elkezdenénk, mondja el, hogyan szólíthatjuk.',
    nameLabel: 'Hogyan szólíthatjuk?',
    nameHint: 'Ezt a nevet a többi családtag is látja. Bármikor módosíthatja a Beállításokban.',
    namePlaceholder: 'pl. Anna',
    nameInvalid: 'Adjon meg egy nevet (1–120 karakter).',
    submit: 'Tovább',
  },

  family: {
    switcher: 'Család',
    create: 'Család létrehozása',
    createTitle: 'Hozza létre a családját',
    createLead:
      'A család a közös tere — a párnak, a gyerekeknek és a nagyszülőknek is. Ön dönti el, ki mit lát.',
    nameLabel: 'A család neve',
    namePlaceholder: 'pl. A mi családunk',
    empty: 'Még nincs egyetlen családja sem.',
  },

  home: {
    /** Greeting; {name} is the signed-in member's first name. */
    greetingMorning: 'Jó reggelt, {name}',
    greetingDay: 'Jó napot, {name}',
    greetingEvening: 'Jó estét, {name}',
    /** Fallback greeting when no name is known yet. */
    greetingNeutral: 'Üdv itthon',
    add: 'Hozzáadás',
    /** Section titles on the at-a-glance dashboard. */
    todayTitle: 'Ma',
    childrenTitle: 'Gyerekek',
    shoppingTitle: 'Bevásárlás',
    expensesTitle: 'Háztartási költségek',
    lastNoteTitle: 'Legutóbbi üzenet',
    /** Small links/summaries under sections. */
    tomorrow: 'Holnap',
    tomorrowEvents: 'Holnap: {count} esemény',
    viewAll: 'Összes megtekintése',
    nothingToday: 'Ma nincs semmi a naptárban.',
    noChildrenYet: 'Itt még nincs egyetlen gyermek sem.',
    noShoppingYet: 'Nincs bevásárlólista.',
    noExpensesYet: 'Ebben a hónapban még nincsenek költségek.',
    noNotesYet: 'Még nincsenek üzenetek.',
    thisMonth: 'Ebben a hónapban',
    /** Expenses summary line, e.g. "3 nevyrovnané". */
    outstandingCount: '{count} rendezetlen',
    /** Empty state — brand-new family with nothing added yet. */
    emptyTitle: 'Üdv itthon 👋',
    emptyLead: 'Kezdje három lépéssel, és az egész család egy helyen lesz.',
    emptyAddChild: 'Adja hozzá az első gyermeket',
    emptyInvitePartner: 'Hívja meg a párját',
    emptyCreateList: 'Hozzon létre egy bevásárlólistát',
  },

  children: {
    title: 'Gyerekek',
    add: 'Gyermek hozzáadása',
    addTitle: 'Gyermek hozzáadása',
    none: 'Itt még nincs egyetlen gyermek sem.',
    noneLead: 'Adja hozzá az első gyermeket — születésnap, egészség és kapcsolatok mind kéznél lesznek.',
    nameLabel: 'Név',
    namePlaceholder: 'pl. Emma',
    emojiLabel: 'Avatár (emoji)',
    birthDateLabel: 'Születési dátum',
    ageYears: 'éves',
    selectPrompt: 'Válasszon gyermeket fent.',
    /** Kid-profile destination: labels for the per-child quick links. */
    profileTitle: 'Gyermek profilja',
    profileLead: 'Minden a gyermekről egy helyen — egyetlen kattintással.',
    quickHealth: 'Egészség',
    quickContacts: 'Kapcsolatok',
    quickCalendar: 'Naptár',
    quickDocuments: 'Dokumentumok',
    birthday: 'Születésnap',
    ageLabel: 'Életkor',
    noBirthDate: 'A születési dátum nincs kitöltve.',
    editTitle: 'Gyermek szerkesztése',
    removeConfirm: 'Biztosan eltávolítja ezt a gyermeket? A bejegyzései megmaradnak.',
  },

  contacts: {
    title: 'Kapcsolatok',
    add: 'Kapcsolat hozzáadása',
    empty: 'Még nincsenek kapcsolatok.',
    typeLabel: 'Típus',
    labelLabel: 'Megnevezés',
    labelPlaceholder: 'pl. Dr. Nagyné',
    phoneLabel: 'Telefon',
    addressLabel: 'Cím',
    types: {
      doctor: 'Orvos',
      school: 'Iskola',
      dentist: 'Fogorvos',
      activity: 'Szakkör',
      other: 'Egyéb',
    },
  },

  health: {
    title: 'Egészségügyi nyilvántartás',
    sensitive: 'Érzékeny adatok (GDPR 9. cikk)',
    lead: 'A fontos egészségügyi információk egy helyen — vészhelyzet esetére.',
    allergies: 'Allergiák',
    medications: 'Gyógyszerek',
    insurance: 'Biztosító',
    bloodType: 'Vércsoport',
    conditions: 'Diagnózisok / állapotok',
    notes: 'Megjegyzések',
    updatedAt: 'Utoljára módosítva',
    placeholderNone: 'Nincs kitöltve',
  },

  calendar: {
    title: 'Naptár',
    add: 'Esemény hozzáadása',
    empty: 'Még nincsenek események.',
    upcoming: 'Közelgő',
    titleLabel: 'Esemény neve',
    typeLabel: 'Típus',
    startLabel: 'Kezdés',
    endLabel: 'Befejezés',
    allDayLabel: 'Egész nap',
    locationLabel: 'Helyszín',
    /** Separated-mode only: the custody sub-view tab label. */
    custodyView: 'Váltott gondoskodás',
    /**
     * Event-type labels. `types` is the family-mode default (no handover/custody
     * surfaced). `typesSeparated` adds the co-parenting types and is selected
     * when `family.mode === 'separated'`. Both carry every key so callers can
     * index either map by the same `CalendarEventType`.
     */
    types: {
      appointment: 'Időpont',
      handover: 'Egyéb',
      custody: 'Egyéb',
      school: 'Iskola',
      activity: 'Tevékenység',
      other: 'Egyéb',
    },
    typesSeparated: {
      appointment: 'Időpont',
      handover: 'Átadás',
      custody: 'Váltás',
      school: 'Iskola',
      activity: 'Tevékenység',
      other: 'Egyéb',
    },
  },

  custody: {
    title: 'Váltási rend',
    none: 'A váltási rend még nincs beállítva.',
    patternLabel: 'Minta',
    startLabel: 'A rend kezdete',
    notesLabel: 'Megjegyzések',
    set: 'Rend beállítása',
    update: 'Rend módosítása',
    parentA: 'A szülő',
    parentB: 'B szülő',
    thisWeek: 'Ezen a héten',
    patterns: {
      'week-week': 'Hét / hét',
      '2-2-3': '2-2-3',
      custom: 'Egyéni',
    },
  },

  documents: {
    title: 'Dokumentumok',
    sensitive: 'Érzékeny',
    add: 'Dokumentum hozzáadása',
    empty: 'Még nincsenek dokumentumok.',
    nameLabel: 'Megnevezés',
    categoryLabel: 'Kategória',
    fileLabel: 'Fájl',
    fileHint: 'Helyi módban a fájl nem töltődik fel — csak a metaadatok mentődnek.',
    uploadedBy: 'Feltöltötte',
    size: 'Méret',
  },

  shopping: {
    title: 'Bevásárlás',
    /** List picker + creation. */
    listLabel: 'Lista',
    newList: 'Új lista',
    newListTitle: 'Új bevásárlólista',
    listNameLabel: 'A lista neve',
    listNamePlaceholder: 'pl. Élelmiszer',
    renameList: 'Lista átnevezése',
    deleteList: 'Lista törlése',
    deleteListConfirm: 'Biztosan törli ezt a listát az összes tételével együtt?',
    noLists: 'Még nincs egyetlen lista sem.',
    noListsLead: 'Hozza létre az első listát — például „Élelmiszer” vagy „Drogéria”.',
    /** Item add + list. */
    addItem: 'Tétel hozzáadása',
    addItemPlaceholder: 'Tétel hozzáadása…',
    itemNameLabel: 'Tétel',
    empty: 'A lista üres.',
    emptyLead: 'Adja hozzá az első tételt fent.',
    /** Category grouping (free-form label on each item). */
    categoryLabel: 'Kategória',
    categoryNone: 'Kategória nélkül',
    categoryPlaceholder: 'pl. Zöldség és gyümölcs',
    /** Assignee = the member responsible for buying the item. */
    assigneeLabel: 'Ki veszi meg',
    assigneeNone: 'Bárki',
    /** Buy-off. */
    bought: 'Megvéve',
    markBought: 'Megjelölés megvettként',
    clearBought: 'Megvett tételek törlése',
    /** {count} → number of checked items to clear. */
    clearBoughtCount: 'Megvett tételek törlése ({count})',
    clearBoughtConfirm: 'Törli az összes megvett tételt erről a listáról?',
    itemCount: '{count} tétel',
  },

  messages: {
    /**
     * "Üzenetek" in family mode (warm) / "Üzenetnapló" in separated (court-log).
     * Bare `title`/`lead`/`empty` default to the family wording; the `*Family` and
     * `*Separated` variants let the mode-aware UI pick explicitly. Under the hood
     * messages stay append-only in BOTH modes.
     */
    title: 'Üzenetek',
    titleFamily: 'Üzenetek',
    titleSeparated: 'Üzenetnapló',
    lead: 'Rövid családi üzenetek és emlékeztetők — például „Hozd el Emmát 15:00-kor”.',
    leadFamily: 'Rövid családi üzenetek és emlékeztetők — például „Hozd el Emmát 15:00-kor”.',
    leadSeparated:
      'Megváltoztathatatlan, bíróságon is felhasználható kommunikációs nyilvántartás. Az üzenetek nem szerkeszthetők és nem törölhetők.',
    placeholder: 'Írjon üzenetet…',
    placeholderSeparated: 'Írjon üzenetet…',
    send: 'Küldés',
    empty: 'Még nincsenek üzenetek. Írja meg az elsőt.',
    emptyFamily: 'Még nincsenek üzenetek. Írja meg az elsőt.',
    emptySeparated: 'A napló még üres. Írja meg az első üzenetet.',
    newest: 'Legújabb',
    /** Shown only in separated mode (the append-only guarantee matters there). */
    appendOnlyNote: 'Csak hozzáfűzhető — szerkesztés és törlés nélkül.',
  },

  expenses: {
    /**
     * "Háztartási költségek" (household budgeting) in family mode vs "Költségek"
     * with split/settle machinery in separated. Bare keys default to family
     * wording; `*Separated` variants carry the co-parenting framing.
     */
    title: 'Háztartási költségek',
    titleFamily: 'Háztartási költségek',
    titleSeparated: 'Költségek',
    lead: 'Áttekintés arról, ki mit fizetett — a háztartás közös költségvetése.',
    leadSeparated: 'A gyerekek közös költségei és azok megosztása a szülők között.',
    add: 'Költség hozzáadása',
    empty: 'Még nincsenek költségek.',
    amountLabel: 'Összeg',
    categoryLabel: 'Kategória',
    descriptionLabel: 'Leírás',
    childLabel: 'Gyermek',
    childAll: 'Az egész család',
    incurredOnLabel: 'Dátum',
    /** Split UI is hidden in family mode; these labels surface in separated. */
    splitLabel: 'Megosztás',
    split5050: 'Fele-fele (50/50)',
    splitCustom: 'Egyéni',
    payerShare: 'Az Ön része',
    otherShare: 'A másik szülő része',
    /** Family mode shows only "kto zaplatil" without any split. */
    paidBy: 'Ki fizette',
    paidByFamily: 'Ki fizette',
    settled: 'Rendezve',
    outstanding: 'Rendezetlen',
    settle: 'Megjelölés rendezettként',
    totalOutstanding: 'Rendezetlen összesen',
    /** Family-mode month total (no settlement concept). */
    totalMonth: 'Összesen ebben a hónapban',
    you: 'Ön',
  },

  agreements: {
    title: 'Megállapodások',
    lead:
      'Írásos megállapodások a szülők között. Az egyik szülő javaslatot tesz, a másik jóváhagyja vagy elutasítja. A jóváhagyott megállapodás megváltoztathatatlan, bíróságon is felhasználható nyilvántartás.',
    propose: 'Megállapodás javaslása',
    proposeTitle: 'Megállapodás javaslása',
    proposeLead:
      'Fogalmazza meg a megállapodást világosan. A másik szülő jóváhagyhatja vagy elutasíthatja.',
    proposeNote:
      'A jóváhagyás után a megállapodás lezárul, és többé nem szerkeszthető vagy törölhető.',
    titleLabel: 'A megállapodás címe',
    titlePlaceholder: 'pl. Nyári szünet 2026',
    bodyLabel: 'A megállapodás szövege',
    bodyPlaceholder: 'Írja le, miben állapodtak meg…',
    empty: 'Még nincsenek megállapodások.',
    emptyLead: 'Javasoljon egy első megállapodást a másik szülőnek.',
    proposedBy: 'Javasolta',
    respondedBy: 'Elintézte',
    accept: 'Jóváhagyás',
    decline: 'Elutasítás',
    withdraw: 'Visszavonás',
    locked: 'Lezárva — további módosítások nélkül.',
    actionStale: 'A megállapodást időközben már elintézték. Frissítettük a listát.',
    you: 'Ön',
    status: {
      proposed: 'Javasolt',
      accepted: 'Jóváhagyott',
      declined: 'Elutasított',
      withdrawn: 'Visszavont',
    },
  },

  sharing: {
    title: 'Család',
    lead:
      'Nézze meg, ki tartozik a családhoz, és döntse el, ki mit lát. A nagyszülőt megbízható személyként meghívhatja — csak azt látja, amit Ön engedélyez.',
    familyNameLabel: 'Család neve',
    rename: 'Átnevezés',
    renameTitle: 'Család átnevezése',
    members: 'Családtagok',
    invite: 'Meghívás',
    inviteMember: 'Tag meghívása',
    inviteTitle: 'Családtag meghívása',
    emailLabel: 'E-mail',
    roleLabel: 'Szerepkör a családban',
    roleParent: 'Szülő',
    roleParentDesc: 'Teljes hozzáférés mindenhez — a másik szülőnek.',
    roleTrusted: 'Megbízható személy',
    roleTrustedDesc:
      'Például nagyszülő — csak azt látja, amit bejelöl.',
    scopesLabel: 'Mit láthat',
    scopesHint:
      'A szülő mindent lát. A megbízható személy csak a bejelölt területeket látja.',
    parentScopesNote:
      'A szülő automatikusan teljes hozzáférést kap minden területhez.',
    sensitiveNote: 'Érzékeny — csak kifejezetten adható meg.',
    seesLabel: 'Látja',
    seesNothing: 'Még nincs hozzáférése egyetlen területhez sem.',
    fullAccess: 'Teljes hozzáférés',
    status: {
      invited: 'Meghívva',
      active: 'Aktív',
    },
    you: 'Ön',
    parentBadge: 'Szülő',
    trustedBadge: 'Megbízható személy',
    changeScopes: 'Hozzáférés módosítása',
    removeMember: 'Eltávolítás',
    removeConfirm: 'Biztosan eltávolítja ezt a személyt a családból?',
    onlyParents: 'Csak a szülők kezelhetik a családtagokat és a hozzáférésüket.',
  },

  scopes: {
    children: 'Gyerekek',
    calendar: 'Naptár',
    contacts: 'Kapcsolatok',
    health: 'Egészség',
    documents: 'Dokumentumok',
    messages: 'Üzenetek',
    expenses: 'Költségek',
    shopping: 'Bevásárlás',
  },

  mode: {
    /** Settings → "Režim rodiny" card. Parent-only, lives at the bottom. */
    cardTitle: 'Családi mód',
    cardLead:
      'A Solandio az együtt élő családokra van beállítva. Ha külön háztartásban élnek, átválthatja a módot — minden adata és a gyerekek teljes előzménye érintetlen marad.',
    /** Current-mode labels. */
    familyLabel: 'Közös háztartás',
    familyDesc: 'Naptár, gyerekek, bevásárlás és költségek az egész családnak.',
    separatedLabel: 'Külön háztartások',
    separatedDesc:
      'Hozzáadja a váltott gondoskodást, a költségek megosztását, a megállapodásokat és az üzenetnaplót.',
    currentLabel: 'Jelenlegi mód',
    /** Switch actions. */
    switchToSeparated: 'Váltás külön háztartásokra',
    switchToFamily: 'Visszaváltás közös háztartásra',
    /** Two-step confirmation dialog (deliberate friction). */
    confirmToSeparatedTitle: 'Átvált a külön háztartások módra?',
    confirmToSeparatedLead:
      'Hozzáadjuk a külön élő szülőknek szóló eszközöket — váltott gondoskodás, költségek megosztása, megállapodások és üzenetnapló.',
    confirmToSeparatedNote:
      'Minden adata és a gyerekek teljes előzménye érintetlen marad. A módot bármikor visszaválthatja.',
    confirmToSeparatedAction: 'Igen, váltok',
    confirmToFamilyTitle: 'Visszavált a közös háztartásra?',
    confirmToFamilyLead:
      'A külön háztartásokhoz tartozó eszközök elrejtődnek. A bejegyzések megmaradnak — a következő váltáskor úgy találja meg őket, ahogy voltak.',
    confirmToFamilyAction: 'Igen, visszaváltok',
    /** Shown while the change is being saved + after it lands. */
    switching: 'Mód váltása…',
    switched: 'A mód megváltozott.',
    /** Gate copy when a non-parent somehow reaches the card. */
    onlyParents: 'A családi módot csak a szülők módosíthatják.',
  },

  audit: {
    title: 'Napló',
    lead: 'Ki, mit és mikor módosított. A nyilvántartás megváltoztathatatlan (csak hozzáfűzhető).',
    empty: 'Még nincsenek bejegyzések.',
    onlyParents: 'A naplót csak a szülők láthatják.',
  },

  errors: {
    forbidden: 'Ehhez a területhez nincs jogosultsága.',
    notFound: 'A tétel nem található.',
    generic: 'A művelet nem sikerült. Próbálja újra.',
  },

  tour: {
    /** Re-invoke action (Settings + help button in the top bar). */
    start: 'Bemutató indítása',
    help: 'Bemutató',
    helpHint: 'A gyors alkalmazás-bemutatót bármikor újra lejátszhatja.',
    /** Joyride control labels. */
    back: 'Vissza',
    next: 'Tovább',
    last: 'Kész',
    skip: 'Kihagyás',
    close: 'Bezárás',
    /** "Krok 2 z 6" — {step} a {steps} doplní Joyride. */
    progress: '{step}. lépés a(z) {steps}-ből',
    steps: {
      welcomeTitle: 'Üdvözöljük a Solandióban',
      welcome: 'Néhány másodperc alatt körbevezetjük.',
      childrenTitle: 'Gyermekek',
      children: 'Itt adhat hozzá gyermeket és válthat közöttük.',
      modulesTitle: 'Minden a családról',
      modules:
        'Balra mindent megtalál a családhoz — naptár, gyerekek, egészség, bevásárlás, költségek, dokumentumok és üzenetek.',
      familyTitle: 'Család',
      family:
        'Itt hívhatja meg a másik szülőt vagy a nagyszülőt, és beállíthatja, mit látnak.',
      settingsTitle: 'Beállítások',
      settings: 'A Beállításokban módosíthatja a nevét és a nyelvet.',
      finishTitle: 'Kész!',
      finish: 'Kezdje egy gyermek vagy egy bevásárlólista hozzáadásával.',
    },
  },
};
